import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContract } from '../../context/ContractContext';
import ContractTypeSelector from './ContractTypeSelector';
import PropertyForm from './PropertyForm';
import PartiesForm from './PartiesForm';
import FinancialForm from './FinancialForm';
import ClausesForm from './ClausesForm';
import ContractPreview from './ContractPreview';
import pdfService from '../../utils/pdfService';
import { ChevronRight, ChevronLeft, FileText, AlertCircle, Eye, EyeOff } from 'lucide-react';

// Componente de Preview do Contrato
const ContractLivePreview = ({ contractData }) => {
  const getContractTypeName = () => {
    const types = {
      'locacao': 'LOCAÇÃO',
      'compra-venda': 'COMPRA E VENDA',
      'administracao': 'ADMINISTRAÇÃO PREDIAL'
    };
    return types[contractData.type] || 'CONTRATO IMOBILIÁRIO';
  };

  const getPropertyTypeName = () => {
    const types = {
      'apartamento': 'apartamento',
      'casa': 'casa',
      'comercial': 'imóvel comercial',
      'terreno': 'terreno',
      'sitio': 'sítio/fazenda'
    };
    return types[contractData.property?.type] || '[tipo do imóvel]';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDocument = (doc, type) => {
    if (!doc) return '[documento]';
    if (type === 'cpf') {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (type === 'cnpj') {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const currentDate = new Date().toLocaleDateString('pt-BR');

  return (
    <div className="bg-white p-6 text-sm leading-relaxed font-serif" style={{ minHeight: '800px' }}>
      {/* Cabeçalho */}
      <div className="text-center mb-6 pb-4 border-b-2 border-gray-800">
        <h1 className="text-lg font-bold uppercase">
          CONTRATO DE {getContractTypeName()}
        </h1>
      </div>

      {/* Data e Local */}
      <div className="text-right mb-6 italic">
        {contractData.property?.city || '[Cidade]'}, {currentDate}
      </div>

      {/* Das Partes */}
      <div className="mb-6">
        <h2 className="text-base font-bold uppercase mb-3 border-b border-gray-600 pb-1">
          Das Partes
        </h2>
        
        <div className="space-y-4">
          <p className="text-justify">
            <strong>LOCADOR/PROPRIETÁRIO:</strong> {contractData.parties?.owner?.name || '[Nome do Proprietário]'}, 
            {' '}{contractData.parties?.owner?.type === 'pf' ? 'CPF' : 'CNPJ'}: {' '}
            {contractData.parties?.owner?.type === 'pf' 
              ? formatDocument(contractData.parties?.owner?.cpf, 'cpf')
              : formatDocument(contractData.parties?.owner?.cnpj, 'cnpj')
            }, residente e domiciliado à {contractData.parties?.owner?.address || '[Endereço do Proprietário]'}, 
            {' '}{contractData.parties?.owner?.city || '[Cidade]'} - {contractData.parties?.owner?.state || '[Estado]'}.
          </p>

          <p className="text-justify">
            <strong>{contractData.type === 'compra-venda' ? 'COMPRADOR' : 'LOCATÁRIO'}:</strong> {' '}
            {contractData.parties?.tenant?.name || '[Nome do Locatário]'}, 
            {' '}{contractData.parties?.tenant?.type === 'pf' ? 'CPF' : 'CNPJ'}: {' '}
            {contractData.parties?.tenant?.type === 'pf' 
              ? formatDocument(contractData.parties?.tenant?.cpf, 'cpf')
              : formatDocument(contractData.parties?.tenant?.cnpj, 'cnpj')
            }, residente e domiciliado à {contractData.parties?.tenant?.address || '[Endereço do Locatário]'}, 
            {' '}{contractData.parties?.tenant?.city || '[Cidade]'} - {contractData.parties?.tenant?.state || '[Estado]'}.
          </p>
        </div>
      </div>

      {/* Do Objeto */}
      <div className="mb-6">
        <h2 className="text-base font-bold uppercase mb-3 border-b border-gray-600 pb-1">
          Do Objeto
        </h2>
        
        <p className="text-justify mb-3">
          O presente contrato tem por objeto um <strong>{getPropertyTypeName()}</strong>, localizado no endereço: {' '}
          <strong>{contractData.property?.address || '[Endereço do Imóvel]'}</strong>, 
          {' '}{contractData.property?.city || '[Cidade]'} - {contractData.property?.state || '[Estado]'}, 
          CEP: {contractData.property?.zipCode || '[CEP]'}.
        </p>

        {contractData.property?.area && (
          <p className="text-justify mb-2">
            <strong>Área:</strong> {contractData.property.area}m².
          </p>
        )}

        {(contractData.property?.bedrooms || contractData.property?.bathrooms || contractData.property?.parking) && (
          <p className="text-justify">
            <strong>Características:</strong> {' '}
            {contractData.property?.bedrooms ? contractData.property.bedrooms + ' quartos' : ''}
            {contractData.property?.bathrooms ? (contractData.property?.bedrooms ? ', ' : '') + contractData.property.bathrooms + ' banheiros' : ''}
            {contractData.property?.parking ? (contractData.property?.bedrooms || contractData.property?.bathrooms ? ', ' : '') + contractData.property.parking + ' vagas de garagem' : ''}.
          </p>
        )}
      </div>

      {/* Das Condições Financeiras */}
      <div className="mb-6">
        <h2 className="text-base font-bold uppercase mb-3 border-b border-gray-600 pb-1">
          Das Condições Financeiras
        </h2>
        
        <div className="space-y-3">
          <p className="text-justify">
            O valor {contractData.type === 'compra-venda' ? 'de venda' : 'do aluguel'} é de {' '}
            <strong>{formatCurrency(contractData.financial?.rentValue)}</strong>
            {contractData.type === 'compra-venda' ? '' : ' mensais'}.
          </p>

          {contractData.financial?.deposit > 0 && (
            <p className="text-justify">
              O valor {contractData.type === 'compra-venda' ? 'de entrada' : 'da caução'} é de {' '}
              <strong>{formatCurrency(contractData.financial.deposit)}</strong>.
            </p>
          )}

          {contractData.financial?.fees && contractData.financial.fees.length > 0 && (
            <div>
              <p className="font-bold mb-2">Taxas Adicionais:</p>
              <div className="border border-gray-400">
                <div className="bg-gray-100 grid grid-cols-4 gap-1 p-2 text-xs font-bold">
                  <div>Taxa</div>
                  <div>Valor</div>
                  <div>Descrição</div>
                  <div>Total</div>
                </div>
                {contractData.financial.fees.map((fee, index) => (
                  <div key={index} className="grid grid-cols-4 gap-1 p-2 text-xs border-t border-gray-300">
                    <div>{fee.name || '[Taxa]'}</div>
                    <div>{formatCurrency(fee.value)}</div>
                    <div>{fee.description || '-'}</div>
                    <div>{formatCurrency(fee.value)}</div>
                  </div>
                ))}
                <div className="bg-gray-100 grid grid-cols-4 gap-1 p-2 text-xs font-bold border-t-2 border-gray-400">
                  <div className="col-span-3">Valor total da mercadoria</div>
                  <div>{formatCurrency(contractData.financial?.rentValue)}</div>
                </div>
                <div className="bg-gray-100 grid grid-cols-4 gap-1 p-2 text-xs font-bold">
                  <div className="col-span-3">Valor total</div>
                  <div>{formatCurrency((parseFloat(contractData.financial?.rentValue) || 0) + (contractData.financial?.fees?.reduce((sum, fee) => sum + (parseFloat(fee.value) || 0), 0) || 0))}</div>
                </div>
              </div>
            </div>
          )}

          {contractData.type === 'locacao' && contractData.financial?.dueDate && (
            <p className="text-justify">
              O pagamento deverá ser efetuado até o dia <strong>{contractData.financial.dueDate}</strong> de cada mês.
            </p>
          )}

          {contractData.type === 'locacao' && contractData.financial?.adjustment && (
            <p className="text-justify">
              O reajuste anual será feito com base no índice <strong>{contractData.financial.adjustment.toUpperCase()}</strong>.
            </p>
          )}
        </div>
      </div>

      {/* Do Prazo */}
      <div className="mb-6">
        <h2 className="text-base font-bold uppercase mb-3 border-b border-gray-600 pb-1">
          Do Prazo
        </h2>
        
        <p className="text-justify">
          {contractData.type === 'locacao' && contractData.financial?.contractDuration
            ? `O presente contrato terá vigência por ${contractData.financial.contractDuration} meses, com início em ___/___/______ e término em ___/___/______.`
            : 'O prazo deste contrato será de _____________, com início em ___/___/______ e término em ___/___/______.'
          }
        </p>
      </div>

      {/* Das Cláusulas Gerais */}
      <div className="mb-8">
        <h2 className="text-base font-bold uppercase mb-3 border-b border-gray-600 pb-1">
          Das Cláusulas Gerais
        </h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-bold text-sm">CLÁUSULA 1ª - DA DESTINAÇÃO</h3>
            <p className="text-justify text-xs">
              {contractData.type === 'compra-venda' 
                ? 'O imóvel destina-se exclusivamente para os fins acordados entre as partes.'
                : 'O imóvel destina-se exclusivamente para fins residenciais, sendo vedada qualquer atividade comercial ou industrial sem prévia autorização por escrito do locador.'
              }
            </p>
          </div>

          <div>
            <h3 className="font-bold text-sm">
              CLÁUSULA 2ª - {contractData.type === 'compra-venda' ? 'DA ENTREGA' : 'DAS OBRIGAÇÕES DO LOCATÁRIO'}
            </h3>
            <p className="text-justify text-xs">
              {contractData.type === 'compra-venda'
                ? 'O imóvel será entregue livre e desembaraçado de quaisquer ônus, encargos ou ocupantes.'
                : 'O locatário se obriga a manter o imóvel em perfeito estado de conservação, sendo responsável por pequenos reparos e manutenção.'
              }
            </p>
          </div>

          <div>
            <h3 className="font-bold text-sm">
              CLÁUSULA 3ª - {contractData.type === 'compra-venda' ? 'DOS IMPOSTOS' : 'DO SEGURO'}
            </h3>
            <p className="text-justify text-xs">
              {contractData.type === 'compra-venda'
                ? 'Todos os impostos, taxas e emolumentos incidentes sobre o imóvel até a data da escritura serão de responsabilidade do vendedor.'
                : 'É obrigatório o seguro contra incêndio do imóvel locado, podendo ser contratado por qualquer das partes.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Assinaturas */}
      <div className="mt-8 pt-6 border-t border-gray-400">
        <p className="text-justify text-xs mb-6">
          E, por estarem justas e contratadas, as partes assinam o presente instrumento em duas vias de igual teor, na presença de duas testemunhas.
        </p>
        
        <div className="grid grid-cols-2 gap-8 mt-8">
          <div className="text-center">
            <div className="border-t border-gray-800 pt-2">
              <p className="text-xs font-semibold">
                {contractData.parties?.owner?.name || '[Nome do Proprietário]'}
              </p>
              <p className="text-xs">
                {contractData.type === 'compra-venda' ? 'VENDEDOR' : 'LOCADOR'}
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <div className="border-t border-gray-800 pt-2">
              <p className="text-xs font-semibold">
                {contractData.parties?.tenant?.name || '[Nome do Locatário]'}
              </p>
              <p className="text-xs">
                {contractData.type === 'compra-venda' ? 'COMPRADOR' : 'LOCATÁRIO'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContractForm = () => {
  const navigate = useNavigate();
  const { contractData } = useContract();
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  const steps = [
    { id: 1, name: 'Tipo de Contrato', component: ContractTypeSelector },
    { id: 2, name: 'Dados do Imóvel', component: PropertyForm },
    { id: 3, name: 'Partes Envolvidas', component: PartiesForm },
    { id: 4, name: 'Condições Financeiras', component: FinancialForm },
    { id: 5, name: 'Cláusulas', component: ClausesForm },
    { id: 6, name: 'Revisão e Finalização', component: ContractPreview }
  ];

  const currentStepData = steps.find(step => step.id === currentStep);
  const CurrentComponent = currentStepData?.component;

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepId) => {
    // Só permite navegar para passos já visitados ou o próximo
    if (stepId <= currentStep || stepId === currentStep + 1) {
      setCurrentStep(stepId);
    }
  };

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1:
        return contractData.type !== '';
      case 2:
        return contractData.property && 
               contractData.property.address && 
               contractData.property.type;
      case 3:
        return contractData.parties && 
               contractData.parties.owner && 
               contractData.parties.owner.name &&
               contractData.parties.owner.cpf &&
               contractData.parties.tenant &&
               contractData.parties.tenant.name &&
               contractData.parties.tenant.cpf;
      case 4:
        return contractData.financial && 
               (contractData.financial.value || contractData.financial.rentValue);
      case 5:
        return stepId < currentStep; // Só marca como completo se já passou por ele
      case 6:
        return stepId < currentStep; // Só marca como completo se já passou por ele
      default:
        return false;
    }
  };

  const canProceed = () => {
    return isStepComplete(currentStep);
  };

  const handleGeneratePDF = async () => {
    setIsGenerating(true);
    setGenerationError(null);
    
    try {
      // Validar se todos os dados necessários estão presentes
      if (!contractData.contractType || !contractData.property || !contractData.parties || !contractData.financial) {
        throw new Error('Dados incompletos para gerar o contrato');
      }

      const result = await pdfService.generateContract(contractData);
      
      if (result.success) {
        // Redireciona para página de sucesso
        navigate('/contratos/sucesso');
      } else {
        setGenerationError(result.message || 'Erro desconhecido ao gerar PDF');
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setGenerationError(error.message || 'Erro ao gerar o contrato. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-dark-900">
                Novo Contrato Imobiliário
              </h1>
              <p className="text-neutral-600 mt-1">
                Preencha as informações para gerar seu contrato
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center px-3 py-2 text-sm text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-neutral-500 hover:text-neutral-700"
              >
                Cancelar
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={step.id > currentStep + 1}
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                      transition-all duration-200 border
                      ${step.id === currentStep 
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : isStepComplete(step.id)
                          ? 'bg-success-500 text-white border-success-500'
                          : step.id <= currentStep + 1
                            ? 'bg-neutral-200 text-neutral-600 hover:bg-neutral-300 border-neutral-300'
                            : 'bg-neutral-100 text-neutral-400 cursor-not-allowed border-neutral-200'
                      }
                    `}
                  >
                    {isStepComplete(step.id) && step.id !== currentStep ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.id
                    )}
                  </button>
                  <span className={`
                    mt-2 text-xs text-center max-w-20
                    ${step.id === currentStep ? 'text-primary-600 font-medium' : 'text-neutral-500'}
                  `}>
                    {step.name}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`
                    flex-1 h-0.5 mx-4 transition-colors duration-200
                    ${step.id < currentStep ? 'bg-success-500' : 'bg-neutral-200'}
                  `} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content with Preview */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Form Section */}
          <div className={`${showPreview ? 'w-1/2' : 'w-full max-w-4xl mx-auto'} transition-all duration-300`}>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-dark-900 mb-2">
                  {currentStepData?.name}
                </h2>
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div 
                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / steps.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Render Current Step Component */}
              {CurrentComponent && (
                <CurrentComponent 
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  canProceed={canProceed()}
                />
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className={`
                    flex items-center px-4 py-2 rounded-lg transition-colors
                    ${currentStep === 1 
                      ? 'text-neutral-400 cursor-not-allowed bg-neutral-100' 
                      : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 bg-white border border-neutral-300'
                    }
                  `}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </button>

                <div className="flex flex-col items-end space-y-2">
                  {/* Erro de geração */}
                  {generationError && (
                    <div className="flex items-center text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg border border-red-200">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>{generationError}</span>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    {currentStep === steps.length ? (
                      <button
                        onClick={handleGeneratePDF}
                        disabled={isGenerating || !canProceed()}
                        className={`
                          flex items-center px-6 py-3 rounded-lg font-medium transition-all
                          ${canProceed() && !isGenerating
                            ? 'bg-success-500 hover:bg-success-600 text-white shadow-sm'
                            : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                          }
                        `}
                      >
                        {isGenerating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Gerando PDF...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            Gerar Contrato
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={handleNext}
                        disabled={!canProceed()}
                        className={`
                          flex items-center px-6 py-3 rounded-lg font-medium transition-all
                          ${canProceed()
                            ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-sm'
                            : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                          }
                        `}
                      >
                        Próximo
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="w-1/2">
              <div className="sticky top-8">
                <div className="bg-white rounded-lg shadow-sm border">
                  <div className="bg-gray-50 px-4 py-3 border-b rounded-t-lg">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <Eye className="w-5 h-5 mr-2" />
                      Preview do Documento
                    </h3>
                  </div>
                  <div className="max-h-[800px] overflow-y-auto">
                    <ContractLivePreview contractData={contractData} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractForm;